import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { AxiosError } from 'axios';
import { toast } from 'sonner';
import { Pencil, Plus, Trash2 } from 'lucide-react';
import {
  createDesignationSchema,
  type ApiError,
  type CreateDesignationInput,
  type Designation,
} from '@pms/shared';
import { designationsService } from '@/services/designations.service';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { DataTable, type Column } from '@/components/ui/data-table';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

interface FormState {
  open: boolean;
  editing: Designation | null;
}

function getErrorMessage(e: unknown): string {
  const ax = e as AxiosError<ApiError>;
  return ax.response?.data?.error?.message ?? 'Something went wrong';
}

export function DesignationsSection() {
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [form, setForm] = useState<FormState>({ open: false, editing: null });
  const [pendingDeactivate, setPendingDeactivate] = useState<{ id: string; name: string } | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['designations', { page, q: search }],
    queryFn: () => designationsService.list({ page, limit: 20, q: search || undefined }),
  });

  const deactivateMutation = useMutation({
    mutationFn: (id: string) => designationsService.deactivate(id),
    onSuccess: () => {
      toast.success('Designation deactivated');
      void queryClient.invalidateQueries({ queryKey: ['designations'] });
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const columns: Column<Designation>[] = [
    { key: 'code', header: 'Code', cell: (d) => <span className="font-mono text-xs">{d.code}</span> },
    { key: 'name', header: 'Name', cell: (d) => <span className="font-medium">{d.name}</span> },
    {
      key: 'level',
      header: 'Level',
      cell: (d) => <span className="text-muted-foreground">{d.level ?? '—'}</span>,
    },
    {
      key: 'is_active',
      header: 'Status',
      cell: (d) => (
        <span
          className={
            d.is_active
              ? 'inline-flex rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-700'
              : 'inline-flex rounded-full bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground'
          }
        >
          {d.is_active ? 'Active' : 'Inactive'}
        </span>
      ),
    },
    {
      key: 'actions',
      header: '',
      className: 'w-24 text-right',
      cell: (d) => (
        <div className="flex justify-end gap-1">
          <Button size="icon" variant="ghost" onClick={() => setForm({ open: true, editing: d })} aria-label="Edit">
            <Pencil className="h-4 w-4" />
          </Button>
          <Button
            size="icon"
            variant="ghost"
            onClick={() => setPendingDeactivate({ id: d.id, name: d.name })}
            disabled={!d.is_active}
            aria-label="Deactivate"
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3">
        <Input
          placeholder="Search by code or name…"
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(1);
          }}
          className="max-w-sm"
        />
        <Button onClick={() => setForm({ open: true, editing: null })}>
          <Plus className="h-4 w-4" /> Add Designation
        </Button>
      </div>
      <DataTable
        columns={columns}
        rows={data?.data ?? []}
        rowKey={(d) => d.id}
        loading={isLoading}
        empty="No designations yet"
        pagination={{
          page,
          totalPages: data?.pagination.totalPages ?? 1,
          onPageChange: setPage,
        }}
      />

      <FormDialog
        state={form}
        onClose={() => setForm({ open: false, editing: null })}
        onSaved={() => {
          setForm({ open: false, editing: null });
          void queryClient.invalidateQueries({ queryKey: ['designations'] });
        }}
      />

      <ConfirmDialog
        open={!!pendingDeactivate}
        onOpenChange={(o) => { if (!o) setPendingDeactivate(null); }}
        title={`Deactivate "${pendingDeactivate?.name}"?`}
        description="The designation will be marked inactive and hidden from new assignments."
        confirmLabel="Deactivate"
        onConfirm={() => { if (pendingDeactivate) deactivateMutation.mutate(pendingDeactivate.id); }}
      />
    </div>
  );
}

function FormDialog({
  state,
  onClose,
  onSaved,
}: {
  state: FormState;
  onClose: () => void;
  onSaved: () => void;
}) {
  const editing = state.editing;
  const { register, handleSubmit, reset, formState } = useForm<CreateDesignationInput>({
    resolver: zodResolver(createDesignationSchema),
    defaultValues: { code: '', name: '' },
    values: editing
      ? { code: editing.code, name: editing.name, level: editing.level ?? undefined }
      : { code: '', name: '' },
  });

  const mutation = useMutation({
    mutationFn: async (input: CreateDesignationInput) => {
      if (editing) {
        await designationsService.update(editing.id, input);
      } else {
        await designationsService.create(input);
      }
    },
    onSuccess: () => {
      toast.success(editing ? 'Designation updated' : 'Designation created');
      reset();
      onSaved();
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  return (
    <Dialog
      open={state.open}
      onOpenChange={(open) => {
        if (!open) {
          reset();
          onClose();
        }
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{editing ? 'Edit Designation' : 'New Designation'}</DialogTitle>
          <DialogDescription>Designations are job titles (Engineer, Manager, etc.).</DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit((v) => mutation.mutate(v))} className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="code">Code</Label>
            <Input id="code" placeholder="e.g. SE" {...register('code')} disabled={!!editing} />
            {formState.errors.code && (
              <p className="text-xs text-destructive">{formState.errors.code.message}</p>
            )}
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="name">Name</Label>
            <Input id="name" placeholder="e.g. Software Engineer" {...register('name')} />
            {formState.errors.name && (
              <p className="text-xs text-destructive">{formState.errors.name.message}</p>
            )}
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="level">Level (optional)</Label>
            <Input
              id="level"
              type="number"
              min={0}
              max={100}
              placeholder="0–100"
              {...register('level', { setValueAs: (v) => (v === '' ? null : Number(v)) })}
            />
          </div>
          <DialogFooter className="gap-2 pt-2">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={mutation.isPending}>
              {mutation.isPending ? 'Saving…' : 'Save'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
